// usePhysicianEmailStatement.ts
import { useMutation } from 'react-query' // v3
import type { AxiosError, AxiosInstance } from 'axios'
import { axiosInstance } from './axiosConfig'

export interface usePhysicianEmailStatementProps {
  onSuccess?: () => void
  onError?: (error: EmailStatementApiError) => void
  axios?: AxiosInstance
  /** Turn on retries if your backend allows re-triggers safely */
  retry?: boolean | number
}

export interface EmailStatementError {
  message?: string
  statementRequestCount?: number
  lastStatementRequestTime?: string
  canRequestStatement?: boolean
  nextAvailableTime?: string
}

export interface ValidationError {
  attribute: string
  errorMessage: string
}

/** Union of what the API might return on error */
export type EmailStatementApiError = AxiosError<
  | EmailStatementError
  | { errors?: ValidationError[]; message?: string }
  | string
>

/**
 * Triggers an email statement for the physician. Many such endpoints return 204/empty body,
 * so this function resolves to void.
 */
const requestPhysicianEmailStatement = async (
  physicianId: string,
  instance: AxiosInstance
): Promise<void> => {
  const id = physicianId?.trim()
  if (!id) throw new Error('physicianId is required')
  await instance.get(`/provider/${id}/email-statement`)
}

export const usePhysicianEmailStatement = ({
  onSuccess,
  onError,
  axios = axiosInstance,
  retry = false,
}: usePhysicianEmailStatementProps = {}) => {
  const mutation = useMutation<void, EmailStatementApiError, string>(
    (physicianId) => requestPhysicianEmailStatement(physicianId, axios),
    {
      retry,
      onSuccess: () => {
        onSuccess?.()
      },
      onError: (error) => {
        onError?.(error)
      },
    }
  )

  return {
    requestEmailStatementAsync: mutation.mutateAsync,
    /** state */
    isLoading: mutation.isLoading,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    /** helpers */
    reset: mutation.reset,
    status: mutation.status,
  }
}
